/**
 * Converts an array of objects into a record indexed by a specified key.
 * @example
 * const data = [
 *   { id: 'a1', name: 'Item 1' },
 *   { id: 'b2', name: 'Item 2' },
 * ];
 * const record = arrayToRecordByKey(data, 'id');
 * // record = {
 * //   a1: { id: 'a1', name: 'Item 1' },
 * //   b2: { id: 'b2', name: 'Item 2' },
 * // }
 *
 * @param {Array<T>} array - array of objects
 * @param {K extends keyof T} key - key as `string` from which to index by, must exist on T
 *
 * @return {Record<T[K], T>} - record indexed by the specified key
 */
export const arrayToRecordByKey = <T extends Record<K, string>, K extends keyof T>(
  array: T[],
  key: K
): Record<T[K], T> => {
  return Object.fromEntries(array.map((item) => [item[key], item])) as Record<T[K], T>;
};

/**
 * Compares two arrays for shallow equality.
 *
 * @description
 * Performs a shallow comparison by checking length equality and comparing each element
 * using strict equality (===). Suitable for flat arrays of primitives (strings, numbers, booleans).
 *
 * @note Limitations:
 * - Uses shallow comparison (=== for each element)
 * - Does not work for nested objects/arrays
 * - Does not handle NaN correctly (NaN !== NaN)
 * - For deep comparison, use lodash's _.isEqual() or fast-deep-equal
 *
 * @template T - The type of elements in the arrays
 *
 * @param {T[]} arr1 - First array to compare
 * @param {T[]} arr2 - Second array to compare
 *
 * @return {boolean} - True if arrays have same length and all elements match, false otherwise
 *
 * @example
 * // Primitives - works well
 * areArraysEqual([1, 2, 3], [1, 2, 3]);  // true
 * areArraysEqual(['a', 'b'], ['a', 'b']);  // true
 * areArraysEqual([1, 2], [1, 3]);  // false
 *
 * @example
 * // Objects - compares by reference only
 * areArraysEqual([{a: 1}], [{a: 1}]);  // false (different references)
 *
 * @example
 * // NaN edge case
 * areArraysEqual([NaN], [NaN]);  // false (NaN !== NaN)
 */
export const areArraysEqual = <T>(arr1: T[], arr2: T[]): boolean => {
  return arr1.length === arr2.length && arr1.every((val, i) => val === arr2[i]);
};
